import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [systemHealth, setSystemHealth] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user, loading: authLoading, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated && !authLoading && user?.role === 'Admin') {
      console.log('Admin authentication complete, fetching admin data...');
      fetchAdminData();
    }
  }, [isAuthenticated, authLoading, user]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      console.log('Making admin dashboard requests...');
      const [statsRes, usersRes] = await Promise.all([
        axios.get('/dashboard/stats'),
        axios.get('/users')
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setRecentUsers(usersRes.data.slice(-5).reverse());
      
      const total = statsRes.data.total_issues;
      const critical = statsRes.data.critical_priority_issues;
      const open = statsRes.data.open_issues;
      
      setSystemHealth({
        status: critical > 0 ? 'critical' : open > total * 0.7 ? 'warning' : 'healthy',
        criticalIssues: critical,
        openIssueRatio: total > 0 ? ((open / total) * 100).toFixed(1) : 0,
        totalUsers: usersRes.data.length,
        activeUsers: usersRes.data.filter(u => u.is_active).length
      });
      
      console.log('Admin dashboard data loaded successfully');
    } catch (error) {
      setError('Failed to load admin data');
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserStatusToggle = async (userId, currentStatus) => {
    try {
      await axios.put(`/users/${userId}/status`, {
        is_active: !currentStatus
      });
      fetchAdminData(); // Refresh data
    } catch (error) {
      alert('Failed to update user status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy': return '🟢';
      case 'warning': return '🟡';
      case 'critical': return '🔴';
      default: return '⚪';
    }
  };

  if (user?.role !== 'Admin') {
    return (
      <div className="main-content">
        <div className="alert alert-danger">
          <h3>🚫 Access Denied</h3>
          <p><strong>Admin privileges required.</strong></p>
          <p>Your current role: <code>{user?.role || 'Unknown'}</code></p>
          <p>You need Admin role to access this page.</p>
          <Link to="/dashboard" className="btn btn-primary">
            Go to Your Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="main-content">
        <div className="admin-loading">
          <div className="spinner-large"></div>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content">
        <div className="alert alert-danger">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="main-content admin-dashboard">
      {/* Admin Header */}
      <div className="admin-header">
        <div className="admin-title">
          <h1>🛡️ Admin Dashboard</h1>
          <p>System Overview & Management</p>
        </div>
        <div className="admin-actions">
          <Link to="/admin/users" className="btn btn-primary">
            <span>👥</span> Manage Users
          </Link>
          <Link to="/admin/settings" className="btn btn-outline">
            <span>⚙️</span> Settings
          </Link>
        </div>
      </div>

      {/* System Health */}
      <div className="system-health-card">
        <div className="health-header">
          <h2>🏥 System Health</h2>
          <div className={`health-status ${getHealthColor(systemHealth.status)}`}>
            {getHealthIcon(systemHealth.status)} 
            {systemHealth.status?.toUpperCase()}
          </div>
        </div>
        <div className="health-metrics">
          <div className="health-metric">
            <span className="metric-value">{systemHealth.criticalIssues}</span>
            <span className="metric-label">Critical Issues</span>
          </div>
          <div className="health-metric">
            <span className="metric-value">{systemHealth.openIssueRatio}%</span>
            <span className="metric-label">Open Issues</span>
          </div>
          <div className="health-metric">
            <span className="metric-value">{systemHealth.activeUsers}/{systemHealth.totalUsers}</span>
            <span className="metric-label">Active Users</span>
          </div>
        </div>
      </div>

      {/* Enhanced Stats Grid */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card critical-card">
          <div className="stat-header">
            <span className="stat-icon">🚨</span>
            <div>
              <h3>Critical Issues</h3>
              <p>Requiring immediate attention</p>
            </div>
          </div>
          <div className="stat-value critical">{stats?.critical_priority_issues || 0}</div>
          <Link to="/issues?priority=Critical" className="stat-action">
            View Critical Issues →
          </Link>
        </div>

        <div className="admin-stat-card users-card">
          <div className="stat-header">
            <span className="stat-icon">👥</span>
            <div>
              <h3>Total Users</h3>
              <p>Registered in system</p>
            </div>
          </div>
          <div className="stat-value">{users.length}</div>
          <Link to="/admin/users" className="stat-action">
            Manage Users →
          </Link>
        </div>

        <div className="admin-stat-card issues-card">
          <div className="stat-header">
            <span className="stat-icon">📋</span>
            <div>
              <h3>Total Issues</h3>
              <p>All time created</p>
            </div>
          </div>
          <div className="stat-value">{stats?.total_issues || 0}</div>
          <Link to="/issues" className="stat-action">
            View All Issues →
          </Link>
        </div>

        <div className="admin-stat-card activity-card">
          <div className="stat-header">
            <span className="stat-icon">⚡</span>
            <div>
              <h3>Recent Activity</h3>
              <p>Last 24 hours</p>
            </div>
          </div>
          <div className="stat-value">{stats?.recent_activities?.length || 0}</div>
          <Link to="/activities" className="stat-action">
            View Activities →
          </Link>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-card">
          <h3>Issue Status Distribution</h3>
          <div className="chart-container">
            <div className="status-bars">
              <div className="status-bar">
                <span className="status-label">Open</span>
                <div className="bar-bg">
                  <div 
                    className="bar-fill open-bar" 
                    style={{width: `${stats ? (stats.open_issues / stats.total_issues * 100) : 0}%`}}
                  ></div>
                </div>
                <span className="status-count">{stats?.open_issues || 0}</span>
              </div>
              <div className="status-bar">
                <span className="status-label">In Progress</span>
                <div className="bar-bg">
                  <div 
                    className="bar-fill progress-bar" 
                    style={{width: `${stats ? (stats.in_progress_issues / stats.total_issues * 100) : 0}%`}}
                  ></div>
                </div>
                <span className="status-count">{stats?.in_progress_issues || 0}</span>
              </div>
              <div className="status-bar">
                <span className="status-label">Closed</span>
                <div className="bar-bg">
                  <div 
                    className="bar-fill closed-bar" 
                    style={{width: `${stats ? (stats.closed_issues / stats.total_issues * 100) : 0}%`}}
                  ></div>
                </div>
                <span className="status-count">{stats?.closed_issues || 0}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="chart-card">
          <h3>User Role Distribution</h3>
          <div className="role-chart">
            {['Admin', 'Developer', 'Tester'].map(role => {
              const count = users.filter(u => u.role === role).length;
              const percentage = users.length > 0 ? (count / users.length * 100) : 0;
              return (
                <div key={role} className="role-bar">
                  <div className="role-info">
                    <span className="role-name">{role}</span>
                    <span className="role-count">{count}</span>
                  </div>
                  <div className="role-progress">
                    <div 
                      className={`role-fill role-${role.toLowerCase()}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Users & Quick Actions */}
      <div className="admin-bottom-section">
        <div className="recent-users-card">
          <div className="card-header">
            <h3>🆕 Recent Users</h3>
            <Link to="/admin/users" className="view-all-link">View All</Link>
          </div>
          <div className="users-list">
            {recentUsers.map(user => (
              <div key={user.id} className="user-item">
                <div className="user-avatar">
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </div>
                <div className="user-info">
                  <div className="user-name">{user.first_name} {user.last_name}</div>
                  <div className="user-details">
                    <span className={`user-role role-${user.role.toLowerCase()}`}>
                      {user.role}
                    </span>
                    <span className="user-date">
                      Joined {formatDate(user.created_at)}
                    </span>
                  </div>
                </div>
                <div className="user-status">
                  <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="quick-actions-card">
          <h3>⚡ Quick Actions</h3>
          <div className="quick-actions-grid">
            <Link to="/admin/users" className="quick-action-btn users-btn">
              <span className="action-icon">👤</span>
              <span className="action-text">Manage Users</span>
            </Link>
            <button className="quick-action-btn broadcast-btn">
              <span className="action-icon">📢</span>
              <span className="action-text">Broadcast</span>
            </button>
            <button className="quick-action-btn backup-btn">
              <span className="action-icon">💾</span>
              <span className="action-text">Backup Data</span>
            </button>
            <Link to="/admin/settings" className="quick-action-btn settings-btn">
              <span className="action-icon">⚙️</span>
              <span className="action-text">System Settings</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 